/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/refs */
import { useEffect, useMemo, useRef, useState } from 'react';

export const useDebounceValue = <T = any>(value: T, delay = 500): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

export interface DebouncedCallback<TCallback extends (...args: any[]) => ReturnType<TCallback>> {
  // The result of the callback, or undefined if we haven't invoked it yet
  (...args: Parameters<TCallback>): ReturnType<TCallback> | undefined;
  // Cancel pending function invocations
  cancel: () => void;
  // Whether we are pending an invocation
  isPending: () => boolean;
}

/**
 * Returns a debounced version of the provided callback function, delaying execution
 * until the provided number of milliseconds have elapsed since the last invocation.
 *
 * NOTE: The debounced callback will return undefined until the actual callback has been invoked.
 * Subsequent calls will return the result of the last invocation.
 */
export const useDebounceCallback = <TCallback extends (...args: any[]) => ReturnType<TCallback>>(
  callback: TCallback,
  delayMs: number,
): DebouncedCallback<TCallback> => {
  const callbackRef = useRef<TCallback>(callback);
  callbackRef.current = callback;

  const isMounted = useRef(true);
  const timerId = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedCallback = useRef<DebouncedCallback<TCallback>>();
  const result = useRef<ReturnType<TCallback>>();

  const debounced = useMemo(() => {
    const wrappedCallback = (...args: Parameters<TCallback>) => {
      if (timerId.current) {
        clearTimeout(timerId.current);
      }

      timerId.current = setTimeout(() => {
        if (isMounted.current) {
          result.current = callbackRef.current(...args);
        }
      }, delayMs);

      return result.current;
    };

    wrappedCallback.cancel = () => {
      if (timerId.current) {
        clearTimeout(timerId.current);
        timerId.current = null;
      }
    };

    wrappedCallback.isPending = () => {
      return !!timerId.current;
    };

    return wrappedCallback;
  }, [delayMs]);

  debouncedCallback.current = debounced;

  useEffect(() => {
    isMounted.current = true;

    return () => {
      debouncedCallback.current?.cancel();
      isMounted.current = false;
    };
  }, []);

  return debounced;
};
